import React, { useState, useEffect } from "react";
import { Navigate, useParams } from "react-router-dom";
import { getProgramReportById } from "../../utils/api/endpoints/program-reports/program-reports";
import PDFEditor from "../../pages/automated-report/PDFEditor";
import LoadingSpinner from "../retests/utils/LoadingSpinner";
import ErrorPage from "../../pages/ErrorPage";

/**
 * Route guard component that only allows QA users to edit pentest reports
 * when the report status is in the allowed list for QA.
 */
const QAPentestReportEditGuard: React.FC = () => {
    const { report_id } = useParams<{ report_id: string }>();
    const [report, setReport] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<any>(null);

    const QA_ALLOWED_STATUSES = [
        "draft",
        "qa_review",
        "business_requested_changes",
        "rejected"
    ];

    useEffect(() => {
        const fetchReport = async () => {
            if (!report_id) return;
            try {
                setIsLoading(true);
                const response = await getProgramReportById(report_id);
                // API returns { status: "success", data: { ...reportData } }
                // We need to extract the actual report data from response.data
                const reportData = response?.data ?? response;
                setReport(reportData);
            } catch (err) {
                console.error("Error fetching pentest report for guard:", err);
                setError(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchReport();
    }, [report_id]);

    // Show loading spinner while fetching report data
    if (isLoading) {
        return <LoadingSpinner />;
    }

    // Show error page if report fetch failed or report not found
    if (error || !report) {
        return <ErrorPage />;
    }

    // Check if QA user has permission to edit this report
    // Backend also enforces this, but this provides a better UX
    // report.status is the actual report workflow status (e.g., "draft", "qa_review")
    const currentStatus = report.status || "draft";
    const canEdit = QA_ALLOWED_STATUSES.includes(currentStatus);

    // If QA doesn't have permission, redirect to the report listing
    if (!canEdit) {
        return <Navigate to="/dashboard/pentest-reports" replace />;
    }

    // If all checks pass, render the PDFEditor
    return <PDFEditor />;
};

export default QAPentestReportEditGuard;
